import axios from "axios";
import { atom, useAtom, useSetAtom } from "jotai";
import cloneDeep from "lodash.clonedeep";
import { traverse } from "object-traversal";
import { useEffect, useState } from "react";
import sift from "sift";
import * as THREE from "three";

/**
 *
 * products
 *
 *
 */

// THIS IS THE GLOBAL products DATA OBJECT
export const products_state = atom({
  activeId: null,
  activeObj: null,
  array: null,
  isPrimed: false,
});

export const default_product_id = "custom_desk";

// function to update products_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_products_activeId = atom(null);

// holds the fetched product array
const all_products_array = atom(null);

/**
 *
 * components
 *
 */

// THIS IS THE GLOBAL components DATA OBJECT
export const components_state = atom({
  activeId: null,
  activeObj: null,
  array: null,
  isPrimed: false,
});

export const default_component_id = "desk_model";

// function to update components_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_components_activeId = atom(null);

// holds the fetched components array
const all_components_array = atom(null);

// *
// sections are referenced in components objects
// *
export const all_sections_array = atom(null);
export const all_parent_sections_array = atom(null);

/**
 *
 * items
 *
 */

// THIS IS THE GLOBAL items DATA OBJECT
export const items_state = atom({
  activeIds: null,
  activeObjs: null,
  array: null,
  isPrimed: false,
});

export const items_state_history = atom([]);

// function to update items_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_items_activeIds = atom(null);

// holds the fetched items array
const all_items_array = atom(null);

// holds the fetched items_list arrays
const items_list_arrays = atom(null);

// holds the fetched non_items array
const non_items_array = atom(null);

/**
 *
 *
 * MISC
 *
 *
 */

export const is_experience_loaded_and_revealed = atom(false);

// Used when we want loading screen for assets not loading by LoadingManager
export const loading_state = atom(false);
export const loading_count = atom(0);
export const update_loading_count = atom(null, (get, set, _arg) => set(loading_count, get(loading_count) + _arg));

// used to know when the caster_wheels are injected into the scene
export const caster_loaded_state = atom(null);

// manipulates desk's height animation
// "still", "up", or "down" are valid states
export const desk_animation_state = atom(null);
export const xm_desk_front_animation_state = atom(null);
export const xm_desk_rear_animation_state = atom(null);

// holds the current desk height to display in the UI
export const desk_height_state = atom(null);
export const xm_desk_front_height_state = atom(null);
export const xm_desk_rear_height_state = atom(null);

// maniuplates dome height animation
// "up", or "down" are valid states
export const dome_animation_state = atom(null);

// maniuplates rollervision animation
// true or false are valid states
export const rollervision_animation_state = atom(true);

// holds the current desk height to display in the UI
// boolean
export const size_overlay_state = atom(null);

export const lookAtTarget_state = atom(new THREE.Vector3(0, 1, 0));

// holds list of mesh that should be outlined via post-processing
// array
export const outlined_mesh_array = atom([]);
export const push_to_outlined_mesh_array = atom(null, (get, set, _arg) => set(outlined_mesh_array, (prev) => [...prev, _arg]));

// used to store a base64 image of the canvas to send to shopping cart
export const canvas_base64 = atom(null);

// create Session Id for this runtime instance that can be used for us to identify errors
const { v4: uuidv4 } = require("uuid");
export const session_id = atom(uuidv4());

// data passed in from the UrlDataController that determines activeId's
export function GlobalDataManagers({
  // products
  products_activeId_fromURL,
  update_products_activeId_inURL,
  // components
  components_activeId_fromURL,
  update_components_activeId_inURL,
  // items
  items_activeIds_fromURL,
  update_items_activeIds_inURL,
}) {
  /**
   *
   * Load the data required to initialize the experience
   *
   */

  const [isInitDataLoaded, setIsInitDataLoaded] = useState(false);
  useEffect(() => {
    loadInitData();
  }, []);
  async function loadInitData() {
    await Promise.all([
      loadProductData(),
      loadComponentsData(),
      loadSectionsData(),
      loadParentSectionsData(),
      loadItemsData(),
      loadNonItemsData(),
      // loadItemsListData()
    ]);
    setIsInitDataLoaded(true);
  }

  /**
   *
   * products
   *
   *
   */

  const [productsState, setProductsState] = useAtom(products_state);
  const [requested_products_activeId, reset_update_products_activeId] = useAtom(update_products_activeId);
  const [allProductsArray, setAllProductsArray] = useAtom(all_products_array);

  // load products for experience to start
  async function loadProductData() {
    let res = await axios("/data/products.json");
    let allProducts = res.data;
    setAllProductsArray(allProducts);
  }

  // when some child tells us to update products_state.activeId, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_products_activeId) {
      update_products_activeId_inURL(requested_products_activeId);
      reset_update_products_activeId(null); // reset this so it'll always register as an update
    }
  }, [requested_products_activeId]);

  // when URL tells us there is a new active product, update products_state
  useEffect(() => {
    if (!isInitDataLoaded || !products_activeId_fromURL) return;
    let productsArray = filterProductData();
    let activeProduct = productsArray.find((productObj) => {
      return productObj._id === products_activeId_fromURL;
    });
    let newProductsState = {
      activeId: products_activeId_fromURL,
      activeObj: activeProduct,
      array: productsArray,
      isPrimed: true,
    };
    setProductsState(newProductsState);
    console.log(`____ NEW productsState ______`, newProductsState);
  }, [products_activeId_fromURL, isInitDataLoaded]);

  // filter and return an array with the active product
  function filterProductData() {
    let applicableProducts = allProductsArray.filter(sift({ _id: { $in: [products_activeId_fromURL] } }));
    return applicableProducts;
  }

  /**
   *
   * components
   *    also, sections are nested inside components
   *    also, items/items_list are nested inside components
   *
   */

  const [componentsState, setComponentsState] = useAtom(components_state);
  const [requested_components_activeId, reset_update_components_activeId] = useAtom(update_components_activeId);
  const [allComponentsArray, setAllComponentsArray] = useAtom(all_components_array);
  const [allSectionsArray, setAllSectionsArray] = useAtom(all_sections_array);
  const [allParentSectionsArray, setAllParentSectionsArray] = useAtom(all_parent_sections_array);
  const [itemsListArrays, setItemsListArrays] = useAtom(items_list_arrays);

  // load components for experience to start
  async function loadComponentsData() {
    let res = await axios("/data/components.json");
    let allComponents = res.data;
    setAllComponentsArray(allComponents);
  }
  async function loadSectionsData() {
    let res = await axios("/data/sections.json");
    let allSections = res.data;
    setAllSectionsArray(allSections);
  }
  async function loadParentSectionsData() {
    let res = await axios("/data/desk-sections.json");
    let allSections = res.data;
    setAllParentSectionsArray(allSections);
  }

  // when some child tells us to update components_state.activeId, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_components_activeId) {
      update_components_activeId_inURL(requested_components_activeId);
      reset_update_components_activeId(null); // reset this so it'll always register as an update
    }
  }, [requested_components_activeId]);

  // when active product changes, we tell the URL to update the active component to be the product's first component
  // unless active component is already set in URL (i.e. shopper returning to a saved config)
  useEffect(() => {
    if (!productsState.activeId || components_activeId_fromURL) return;
    let activeProduct = productsState.array.find((productObj) => productObj._id === productsState.activeId);
    update_components_activeId_inURL(activeProduct.components[0]);
  }, [productsState.activeId]);

  // when URL tells us there is a new active component, update components_state
  useEffect(() => {
    if (!components_activeId_fromURL || !productsState.isPrimed) return;
    let componentChoicesArray = filterComponentData();
    let activeComponent = componentChoicesArray.find((componentObj) => componentObj._id === components_activeId_fromURL);
    let newComponentsState = {
      activeId: components_activeId_fromURL,
      activeObj: activeComponent,
      array: componentChoicesArray,
      isPrimed: true,
    };
    setComponentsState(newComponentsState);
    console.log(`____ NEW componentsState ______`, newComponentsState);
  }, [components_activeId_fromURL, productsState.isPrimed]);

  // filter and return an array with the list of components
  function filterComponentData() {
    let applicableComponentIds = productsState.array.find((productObj) => productObj._id === productsState.activeId).components;
    let applicableComponents = allComponentsArray.filter(sift({ _id: { $in: applicableComponentIds } }));
    // make applicableComponents have same order as product.components array
    applicableComponents.sort((a, b) => {
      return applicableComponentIds?.indexOf(a._id) - applicableComponentIds?.indexOf(b._id);
    });
    // no need to update sections and items again
    if (componentsState.isPrimed) return applicableComponents;
    // inject correct sections objects and items_list id's
    applicableComponents.forEach((componentObj) => {
      // sections
      let sectionIds = componentObj.sections;
      sectionIds?.forEach((sectionId, index) => {
        componentObj.sections[index] = allSectionsArray.find((sectionObj) => sectionObj._id === sectionId);
      });
      // Parent Sections
      let parentSectionIds = componentObj.ParentSections;
      parentSectionIds?.forEach((sectionId, index) => {
        componentObj.ParentSections[index] = allParentSectionsArray.find((sectionObj) => sectionObj._id === sectionId);
      });
      if (componentObj.ParentSections) {
        componentObj.ParentSections.forEach((item, index) => {
          return item.children?.forEach((i, indx) => {
            componentObj.ParentSections[index].children[indx] = allSectionsArray.find((sectionObj) => sectionObj._id === i);
          });
        });
      }
      // items_list
      let expandedItemsArray = [];
      componentObj.items.forEach((itemId, index) => {
        // expand any _items_list
        if (itemId.includes("_items_list")) {
          let itemsArray_fromList = itemsListArrays[itemId];
          expandedItemsArray = expandedItemsArray.concat(itemsArray_fromList);
        } else {
          expandedItemsArray.push(itemId);
        }
      });
      // update component's .items
      componentObj.items = expandedItemsArray;
    });
    return applicableComponents;
  }

  /**
   *
   * items / non_items
   *
   */

  const [itemsState, setItemsState] = useAtom(items_state);
  const setItemsActiveIds = useSetAtom(update_items_activeIds);
  const [itemsStateHistory, setItemsStateHistory] = useAtom(items_state_history);
  const [requested_items_activeIds, reset_update_items_activeId] = useAtom(update_items_activeIds);
  const [allItemsArray, setAllItemsArray] = useAtom(all_items_array);
  const [allNonItemsArray, setAllNonItemsArray] = useAtom(non_items_array);

  function defaultItemActiveIds() {
    let activeIds = {
      desk_model: { _id: "xm8_curved_72" },
      materials: {
        desk_material: { _id: "rock_ash" },
      },
      accessories: {
        array: [{ _id: "no_axys_level" }],
      },
      non_items: {
        environment_state: {
          _id: "home_office",
        },
      },
    };
    return activeIds;
  }

  // load items for experience to start
  async function loadItemsData() {
    let res = await axios("/data/items.json");
    let allItems = res.data;
    setAllItemsArray(allItems);
  }
  async function loadNonItemsData() {
    let res = await axios("/data/non_items.json");
    let allNonItems = res.data;
    setAllNonItemsArray(allNonItems);
  }


  // load items_list for experience to start
  // async function loadItemsListData() {
  //   let [color_items_list, engravings_default_items_list] = await Promise.all([
  //     loadColorItems(),
  //     loadEngravingItems(),
  //   ])
  //   setItemsListArrays({
  //     "color_items_list": color_items_list,
  //     "engravings_default_items_list": engravings_default_items_list,
  //   });
  // }
  // async function loadColorItems() {
  //   let res = await fetch('/data/items_list/color_items_list.json');
  //   let array = await res.json();
  //   return array;
  // }
  // async function loadEngravingItems() {
  //   let res = await fetch('/data/items_list/engravings_default_items_list.json');
  //   let array = await res.json();
  //   return array;
  // }

  // when some child tells us to update items_state.activeIds, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_items_activeIds) {
      let mutated_requested_items_activeIds = handleDefaultItemsForDeskTypes(requested_items_activeIds); // CUSTOM CODE
      update_items_activeIds_inURL(mutated_requested_items_activeIds);
      reset_update_items_activeId(null); // reset this so it'll always register as an update
    }
  }, [requested_items_activeIds]);

  // const updateEnvironment = useCallback(
  //   (id) => {
  //     if (id.includes("battle_station") && itemsState.activeIds.non_items.environment_state._id !== "game_room") {
  //       let copy = { ...itemsState.activeIds };
  //       copy.non_items.environment_state = { _id: "game_room" };
  //       setItemsStateHistory("game_room");
  //       setItemsActiveIds(copy);
  //     }
  //   },
  //   [itemsState.activeIds, setItemsActiveIds]
  // );

  // useEffect(() => {
  //   if (itemsStateHistory[1] !== itemsState?.activeIds?.desk_model?._id) {
  //     updateEnvironment(itemsState?.activeIds?.desk_model?._id);
  //   }
  // }, [itemsState, itemsStateHistory, updateEnvironment]);

  // Utility: Remove excluded accessories from activeIds
  function filterOutExcludedAccessories(items_activeIds, itemsArray) {
    if (!items_activeIds.accessories || !items_activeIds.accessories.array) return items_activeIds;
    items_activeIds.accessories.array = items_activeIds.accessories.array.filter((acc) => {
      const item = itemsArray.find((i) => i._id === acc._id);
      return item && !item.excluded;
    });
    return items_activeIds;
  }

  function handleIncludedItems(items_activeIds, itemsArray) {
    // Handle regular included items
    itemsArray.forEach((item) => {
      if (
        item.metaData?.isIncluded &&
        item.section === "desk_accessories" &&
        !item.excluded && // Only add if not excluded
        !items_activeIds["accessories"].array.find((i) => i._id === item._id)
      ) {
        items_activeIds["accessories"].array.push({ _id: item._id });
      }
    });

    // Handle Axys level included accessories
    const axysLevelItem = items_activeIds.accessories.array.find((item) => item._id?.includes("axys_level") || item._id === "no_axys");
    if (axysLevelItem) {
      const axysLevelObj = itemsArray.find((item) => item._id === axysLevelItem._id);
      if (axysLevelObj && axysLevelObj.includedAccessories && axysLevelObj.includedAccessories.length > 0) {
        // Mark the accessories included in the Axys level with a special flag
        // that indicates they're included but should still load in 3D model
        axysLevelObj.includedAccessories.forEach((accessoryId) => {
          const accessoryObj = itemsArray.find((item) => item._id === accessoryId);
          if (accessoryObj && !accessoryObj.excluded) {
            // Only mark if not excluded
            accessoryObj.metaData = accessoryObj.metaData || {};
            // Use is3dModelDynamicallyInjected:true so the model will load despite being marked as included
            accessoryObj.metaData.isAxysIncluded = true;
            accessoryObj.metaData.is3dModelDynamicallyInjected = true;
          }
        });
      }
    }

    // Always filter out excluded accessories after mutation
    return filterOutExcludedAccessories(items_activeIds, itemsArray);
  }

  // CUSTOM CODE: handles default base materials for different desk types
  function handleDefaultItemsForDeskTypes(requested_items_activeIds) {
    let mutated_requested_items_activeIds = { ...requested_items_activeIds };

    if (mutated_requested_items_activeIds.desk_model._id.slice(0, 2) === "xm") {
      // now make sure the material_editing_status is the desk_material and not base_material, since there's only 1 default base choice
      let material_component = componentsState.array.find((component) => component._id === "materials");
      material_component.material_editing_status = "desk_material";
    }

    return mutated_requested_items_activeIds;
  }

  // when active product changes & components_state is primed, we tell the URL to update the activeIds to each component's default item
  // happens on site load unless activeIds is already set in URL (i.e. shopper returning to a saved config)
  useEffect(() => {
    if (!componentsState.isPrimed || items_activeIds_fromURL) return;
    update_items_activeIds_inURL(defaultItemActiveIds());
  }, [productsState.activeId, componentsState.isPrimed]);

  // Migrates old item IDs to their new consolidated versions
  function migrateConsolidatedItemIds(items_activeIds) {
    // Define mapping of old item IDs to new consolidated IDs
    const itemIdMigrationMap = {
      // Heaters consolidation
      heaters_xm8_curved: "heaters",
      heaters_xm8_straight: "heaters",
      heaters_xm8: "heaters",
      // AXYS Level consolidations
      axys_level_1_xm8_curved: "axys_level_1",
      axys_level_1_xm8_straight: "axys_level_1",
      axys_level_2_xm8_curved: "axys_level_2",
      axys_level_2_xm8_straight: "axys_level_2",
      axys_level_3_xm8_curved: "axys_level_3",
      axys_level_3_xm8_straight: "axys_level_3",
      axys_level_1_xm8: "axys_level_1",
      axys_level_2_xm8: "axys_level_2",
      axys_level_3_xm8: "axys_level_3",
      // Datadock consolidation
      datadock_xm8_curved: "datadock",
      datadock_xm8_straight: "datadock",
      datadock_xm8: "datadock",
      // Desktop texture migrations (old textures to new textures)
      mistral: "sangallo_summit",
      columbia_walnut: "sangallo_mimosa",
      monaco: "sangallo_mimosa",
      cassis: "sangallo_dusk",
      matte_lux_charcoal: "perfect_touch_gray",
      matte_lux_black: "perfect_touch_black",
    };

    // Recursively traverse the items_activeIds object to find and replace old IDs
    function migrateIds(obj) {
      if (Array.isArray(obj)) {
        return obj.map((item) => {
          if (item && typeof item === "object" && item._id) {
            // Check if this item ID needs migration
            const newId = itemIdMigrationMap[item._id];
            if (newId) {
              return { ...item, _id: newId };
            }
          }
          return migrateIds(item);
        });
      } else if (obj && typeof obj === "object") {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key === "_id" && itemIdMigrationMap[value]) {
            result[key] = itemIdMigrationMap[value];
          } else {
            result[key] = migrateIds(value);
          }
        }
        return result;
      }
      return obj;
    }

    return migrateIds(items_activeIds);
  }

  // Ensures that an AXYS level is always selected
  function ensureAxysLevelSelected(items_activeIds, itemsArray, newDeskModelId) {
    // If accessories array doesn't exist, create it
    if (!items_activeIds.accessories || !items_activeIds.accessories.array) {
      items_activeIds.accessories = { array: [] };
    }

    // Check if there's already an AXYS level item selected
    const hasAxysLevelItem = items_activeIds.accessories.array.some((item) => item._id?.includes("axys_level") || item._id === "no_axys_level");

    // If no AXYS level is selected, add the default "no_axys_level"
    if (!hasAxysLevelItem) {
      items_activeIds.accessories.array.push({ _id: "no_axys_level" });
    } else {
      // Check if the currently selected AXYS level is compatible with the new desk model
      const currentAxysLevelItem = items_activeIds.accessories.array.find((item) => item._id?.includes("axys_level") || item._id === "no_axys_level");

      if (currentAxysLevelItem && currentAxysLevelItem._id !== "no_axys_level") {
        const axysLevelObj = itemsArray.find((item) => item._id === currentAxysLevelItem._id);

        // Check if the AXYS level has dependencies that would exclude it for the current desk model
        if (axysLevelObj && axysLevelObj.dependencies && axysLevelObj.dependencies.length > 0) {
          const shouldBeExcluded = axysLevelObj.dependencies.some((dep) => {
            if (dep.itemValues.includes(newDeskModelId) && dep.updates && dep.updates.excluded === true) {
              return true;
            }
            return false;
          });

          // If the current AXYS level should be excluded with the new desk model, switch to "no_axys_level"
          if (shouldBeExcluded) {
            // Find the index of the current AXYS level item
            const axysLevelIndex = items_activeIds.accessories.array.findIndex((item) => item._id?.includes("axys_level") || item._id === "no_axys_level");

            // Replace it with "no_axys_level"
            if (axysLevelIndex !== -1) {
              items_activeIds.accessories.array[axysLevelIndex] = { _id: "no_axys_level" };
            }
          }
        }
      }
    }

    // Always filter out excluded accessories after mutation
    return filterOutExcludedAccessories(items_activeIds, itemsArray);
  }

  // when URL tells us there is a new items.activeIds, update items_state
  useEffect(() => {
    if (!items_activeIds_fromURL || !componentsState.isPrimed) return;
    let items_activeIds_clone = cloneDeep(items_activeIds_fromURL);
    let newItemsArray = getUpdatedItemsArray(items_activeIds_clone);

    // Ensure an AXYS level is selected
    items_activeIds_clone = ensureAxysLevelSelected(items_activeIds_clone, newItemsArray, items_activeIds_clone.desk_model._id);

    let mutated_items_activeIds = handleIncludedItems(items_activeIds_clone, newItemsArray);
    // Migrate old item IDs to new consolidated versions
    mutated_items_activeIds = migrateConsolidatedItemIds(mutated_items_activeIds);
    // Always filter out excluded accessories after all mutations
    mutated_items_activeIds = filterOutExcludedAccessories(mutated_items_activeIds, newItemsArray);
    let newActiveObjs = getActiveItemObjs(mutated_items_activeIds, newItemsArray);

    let newItemsState = {
      activeIds: mutated_items_activeIds,
      activeObjs: newActiveObjs,
      array: newItemsArray,
      isPrimed: true,
    };
    setItemsState(newItemsState);
    setItemsStateHistory((history) => {
      return [newItemsState.activeIds.desk_model._id, ...history].slice(0, 2);
    });
    console.log(`____ NEW itemsState ______`, newItemsState);
  }, [items_activeIds_fromURL, componentsState.isPrimed]);

  function getActiveItemObjs(activeIds, itemsArray) {
    let activeObjs = cloneDeep(activeIds);
    let mods = {};

    const updateObjViaPaths = (objToUpdate, mods) => {
      for (var path in mods) {
        var k = objToUpdate;
        var steps = path.split(".");
        steps.pop(); // removing the _id entry so we replace whole object
        var last = steps.pop();
        steps.forEach((e) => (k[e] = k[e] || {}) && (k = k[e]));
        k[last] = mods[path];
      }
      return objToUpdate;
    };

    traverse(activeObjs, (context) => {
      let { parent, key, value, meta } = context;
      if (key === "_id") {
        // we've found an abbreviated item obj
        // add its path and the full item obj to mods
        mods[meta.currentPath] = {
          ...itemsArray.find((item) => item._id === value),
        };
      }
    });

    activeObjs = updateObjViaPaths(activeObjs, mods);
    return activeObjs;
  }

  // traverses activeIds obj and finds any inputs
  // uses those inputs to update item in itemsArray
  // also applies updates from dependencies
  function getUpdatedItemsArray(activeIds) {
    let itemsArrayCopy;
    // prime the itemsState.array if not already present
    if (!itemsState.array) itemsArrayCopy = getApplicableItemsArray();
    // use copy of itemsState.array so any existing inputs or edits are copied
    else itemsArrayCopy = itemsState.array;

    // makes array of activeId's for use in updateItemFromDependencies
    let activeIdArray = [];
    traverse(activeIds, (context) => {
      const { parent, key, value } = context;
      if (key === "_id") activeIdArray.push(value);
      // update items from inputs
      if (key === "inputs" && value) {
        let item = itemsArrayCopy.find((obj) => obj._id === parent._id);
        updateItemWithInputs(item, value);
      }
    });

    // update items from dependencies
    itemsArrayCopy.forEach((item) => {
      updateItemFromDependencies(item, activeIdArray);
    });

    return itemsArrayCopy;
  }

  function getApplicableItemsArray() {
    // make array of all applicable item id's
    let itemIdArray = [];
    componentsState.array.forEach((component) => {
      itemIdArray = itemIdArray.concat(component.items);
    });
    // filter the allItemsArray to only include the applicable ones according to itemIdArray
    let filteredItemsArray = allItemsArray.filter(sift({ _id: { $in: itemIdArray } }));

    // if there are non_items, add those to the items array
    if (allNonItemsArray) filteredItemsArray = filteredItemsArray.concat(allNonItemsArray);

    return filteredItemsArray;
  }

  // updates the item specified with the new inputs
  function updateItemWithInputs(item, newInputObj) {
    // pasting the newInputObj to item.inputs
    item.inputs = newInputObj;
    // traverse new inputs
    Object.entries(newInputObj).forEach(([inputKey, inputValue]) => {
      // traverse item
      traverse(item, (context) => {
        const { parent, key, value, meta } = context;
        // update item values with input values
        if (inputKey == key && !meta.currentPath.includes("inputs") && inputValue !== null) {
          parent[key] = inputValue;
        }
      });
    });
  }

  /**
   * Updates item properties based on dependency rules and active item selections
   *
   * Two modes for dependency handling:
   *
   * 1. DEFAULT MODE (original behavior):
   *    - Items are not excluded by default (excluded=false)
   *    - Dependencies specify when items should be excluded
   *    - Use when most configurations SHOULD include the item
   *
   * 2. EXCLUSION-BY-DEFAULT MODE (new behavior):
   *    - Items are excluded by default (defaultExcluded=true, excluded=true)
   *    - Dependencies with inclusionMode=true specify when items should be included
   *    - Use when most configurations should NOT include the item
   *
   * Item properties:
   * - defaultExcluded: Boolean, if true item is excluded by default
   *
   * Dependency properties:
   * - inclusionMode: Boolean, if true this dependency specifies when to include the item
   *   otherwise it specifies when to exclude the item
   * - itemValues: Array of item IDs that trigger this dependency
   * - updates: Object with property updates to apply when dependency conditions match
   *
   * @param {Object} item - The item to update
   * @param {Array} activeIdArray - Array of active item IDs
   */
  function updateItemFromDependencies(item, activeIdArray) {
    if (item.dependencies && item.dependencies.length > 0) {
      // New property: defaultExcluded determines whether item should be excluded by default
      // If true, the item is excluded unless explicitly included by a dependency
      // If false or not present, item follows the original behavior (not excluded by default)
      const isDefaultExcluded = item.defaultExcluded === true;

      // Set initial excluded state based on defaultExcluded property
      if (isDefaultExcluded) {
        // Start with excluded=true if defaultExcluded is set
        item.excluded = true;
      } else {
        // Original behavior: reset excluded to false by default
        if (item.excluded) item.excluded = false;
      }

      // iterate the dependency objects
      item.dependencies.forEach((dependencyObj) => {
        // check if an active item._id is in this dependency object's .itemValues
        const hasMatchingItem = dependencyObj.itemValues.some((itemId) => activeIdArray.includes(itemId));

        if (hasMatchingItem) {
          // apply the dependency updates to the item
          Object.entries(dependencyObj.updates).forEach(([keyToUpdate, newValue]) => {
            item[keyToUpdate] = newValue;
          });
        }
      });
    }
  }

  return null;
}
